import {
  entityDomainKey,
  type AuditEntity,
  type EntityConfidence,
  type EntitySource,
} from '@/lib/radar/entity-types'

export interface DetectedBrand extends AuditEntity {
  category: string
  keywords: string
  description: string
}

const ENTITY_SOURCES: ReadonlySet<EntitySource> = new Set([
  'jsonld',
  'og_site_name',
  'title',
  'model_grounded',
  'domain_fallback',
  'user_confirmed',
])

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function isEntitySource(value: unknown): value is EntitySource {
  return typeof value === 'string' && ENTITY_SOURCES.has(value as EntitySource)
}

function entitySource(value: unknown): EntitySource {
  return isEntitySource(value) ? value : 'domain_fallback'
}

function entityConfidence(value: unknown): EntityConfidence {
  return value === 'high' || value === 'medium' || value === 'low'
    ? value
    : 'low'
}

function entityEvidence(value: unknown): AuditEntity['evidence'] {
  if (!Array.isArray(value)) return []
  return value.slice(0, 20).flatMap(item => {
    if (!item || typeof item !== 'object') return []
    const evidence = item as Record<string, unknown>
    if (
      !isEntitySource(evidence.source) ||
      typeof evidence.value !== 'string' ||
      typeof evidence.weight !== 'number' ||
      !Number.isFinite(evidence.weight)
    ) {
      return []
    }
    return [
      {
        source: evidence.source,
        value: evidence.value,
        weight: evidence.weight,
        ...(typeof evidence.detail === 'string'
          ? { detail: evidence.detail }
          : {}),
      },
    ]
  })
}

function entityConflicts(value: unknown): AuditEntity['conflicts'] {
  if (!Array.isArray(value)) return []
  return value.slice(0, 20).flatMap(item => {
    if (!item || typeof item !== 'object') return []
    const conflict = item as Record<string, unknown>
    const between = conflict.between
    const values = conflict.values
    if (
      !Array.isArray(between) ||
      between.length !== 2 ||
      !isEntitySource(between[0]) ||
      !isEntitySource(between[1]) ||
      !Array.isArray(values) ||
      values.length !== 2 ||
      typeof values[0] !== 'string' ||
      typeof values[1] !== 'string' ||
      typeof conflict.note !== 'string'
    ) {
      return []
    }
    return [
      {
        between: [between[0], between[1]],
        values: [values[0], values[1]],
        note: conflict.note,
      },
    ]
  })
}

/**
 * Client helper: infer brand/category/keywords from a domain via the public,
 * rate-limited /api/tools/detect-brand endpoint. Lets the brand-context free
 * tools (YouTube, Citation, Reddit) accept a domain instead of a typed brand,
 * collapsing them to the same URL-only friction as crawl-check.
 *
 * Returns null on empty input, an invalid URL, or any failure, so callers
 * fall back to manual brand entry rather than blocking. Mirrors the platform
 * dashboard's detectBrand flow.
 */
export async function detectBrandFromDomain(
  input: string
): Promise<DetectedBrand | null> {
  const raw = input.trim()
  if (!raw) return null

  let normalized = raw
  if (!normalized.startsWith('http://') && !normalized.startsWith('https://')) {
    normalized = `https://${normalized}`
  }
  try {
    // Reject anything that is not a real URL (e.g. a plain brand name).
    new URL(normalized)
  } catch {
    return null
  }

  try {
    const res = await fetch('/api/tools/detect-brand', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url: normalized }),
    })
    if (!res.ok) return null
    const rawData: unknown = await res.json()
    const data =
      rawData && typeof rawData === 'object'
        ? (rawData as Record<string, unknown>)
        : {}
    const brand = stringValue(data.brand)
    const confidence = entityConfidence(data.confidence)
    return {
      domain: entityDomainKey(stringValue(data.domain) || normalized),
      brand,
      legalName: stringValue(data.legalName) || null,
      source: entitySource(data.source),
      confidence,
      // Missing or contradictory provenance must fail closed. A caller may only
      // skip confirmation when the resolver explicitly returned BOTH high
      // confidence and requiresConfirmation: false.
      requiresConfirmation:
        confidence !== 'high' || data.requiresConfirmation !== false,
      evidence: entityEvidence(data.evidence),
      conflicts: entityConflicts(data.conflicts),
      resolverVersion: stringValue(data.resolverVersion) || 'unknown',
      resolvedAt: stringValue(data.resolvedAt) || new Date().toISOString(),
      detectedBrand: brand,
      confirmedByUser: false,
      category: stringValue(data.category),
      keywords: stringValue(data.keywords),
      description: stringValue(data.description),
    }
  } catch {
    return null
  }
}
